import { colorsNames } from '../colors';
import { variants, alignments } from '../components/text/text';
import { pascalCaseToKebabCase } from '../utilities';
import { fontWeights, textSizes, shadows, radii, spaces } from '../variables';

const sides = ['top', 'bottom', 'left', 'right'];

const spacesWithZero = { 0: '0', ...spaces };

const spacesWithAutoAndZero = { 0: '0', auto: 'auto', ...spaces };

export const colorRules = colorsNames.map(colorsName => {
  return {
    selector: 'c',
    modifier: colorsName,
    declarations: [
      {
        property: 'color',
        value: `var(--lns-color-${colorsName})`,
      },
    ],
  };
});

export const backgroundColorRules = colorsNames.map(colorsName => {
  return {
    selector: 'bgc',
    modifier: colorsName,
    declarations: [
      {
        property: 'background-color',
        value: `var(--lns-color-${colorsName})`,
      },
    ],
  };
});

export const textSizeRules = Object.keys(textSizes).map(size => {
  return {
    selector: 'text',
    modifier: size,
    declarations: [
      {
        property: 'font-size',
        value: `var(--lns-fontSize-${size})`,
      },
      {
        property: 'line-height',
        value: `var(--lns-lineHeight-${size})`,
      },
      {
        property: 'letter-spacing',
        value: `var(--lns-letterSpacing-${size})`,
      },
      size.includes('heading-') || size.includes('xlarge')
        ? {
            property: 'font-weight',
            value: 'var(--lns-fontWeight-bold)',
          }
        : {
            property: 'font-weight',
            value: 'var(--lns-fontWeight-regular)',
          },
    ],
  };
});

export const fontWeightRules = Object.keys(fontWeights).map(fontWeight => {
  return {
    selector: 'weight',
    modifier: fontWeight,
    declarations: [
      {
        property: 'font-weight',
        value: `var(--lns-fontWeight-${fontWeight})`,
      },
    ],
  };
});

export const textVariantRules = Object.entries(variants).map(([key, value]) => {
  return {
    selector: 'text',
    modifier: key,
    declarations: [
      {
        property: 'font-size',
        value: `var(--lns-fontSize-${value.size})`,
      },
      {
        property: 'line-height',
        value: `var(--lns-lineHeight-${value.size})`,
      },
      {
        property: 'font-weight',
        value: `var(--lns-fontWeight-${value.fontWeight})`,
      },
    ],
  };
});

export const textAlignmentRules = alignments.map(alignment => {
  return {
    selector: 'text',
    modifier: alignment,
    declarations: [
      {
        property: 'text-align',
        value: alignment,
      },
    ],
  };
});

export const shadowRules = Object.keys(shadows).map(shadow => {
  return {
    selector: 'shadow',
    modifier: shadow,
    declarations: [
      {
        property: 'box-shadow',
        value: `var(--lns-shadow-${shadow})`,
      },
    ],
  };
});

export const radiiRules = Object.keys(radii).map(radius => {
  return {
    selector: 'radius',
    modifier: radius,
    declarations: [
      {
        property: 'border-radius',
        value: `var(--lns-radius-${radius})`,
      },
    ],
  };
});

interface SpacingSelector {
  selector: string;
  property: string;
  modifier: string;
  value: string;
}

export const getSpacingSelectors = (
  property: string,
  sides: string[],
  spaces: any,
  shortSides?: string
): SpacingSelector[] => {
  const result: SpacingSelector[] = [];

  sides.map(side => {
    const sideString = shortSides
      ? `${property.charAt(0)}${side.charAt(0)}`
      : side;

    Object.keys(spaces).map(space => {
      result.push({
        selector: sideString,
        property: `${property}${
          side && property ? `-${side}` : side ? side : ''
        }`,
        modifier: space,
        value:
          space === 'auto' || space === '0'
            ? space
            : `var(--lns-space-${space})`,
      });
    });
  });

  return result;
};

export const marginRules = Object.values(
  getSpacingSelectors(
    'margin',
    ['', ...sides],
    spacesWithAutoAndZero,
    'shortSides'
  )
).map(spaceSelector => {
  return {
    selector: spaceSelector.selector,
    modifier: spaceSelector.modifier,
    declarations: [
      {
        property: spaceSelector.property,
        value: spaceSelector.value,
      },
    ],
  };
});

export const marginCrossRules = Object.values(
  getSpacingSelectors('margin', ['x', 'y'], spacesWithAutoAndZero, 'shortSides')
).map(spaceSelector => {
  return {
    selector: spaceSelector.selector,
    modifier: spaceSelector.modifier,
    declarations: [
      {
        property:
          spaceSelector.property === 'margin-x' ? 'margin-left' : 'margin-top',
        value: spaceSelector.value,
      },
      {
        property:
          spaceSelector.property === 'margin-x'
            ? 'margin-right'
            : 'margin-bottom',
        value: spaceSelector.value,
      },
    ],
  };
});

export const paddingRules = Object.values(
  getSpacingSelectors('padding', ['', ...sides], spacesWithZero, 'shortSides')
).map(spaceSelector => {
  return {
    selector: spaceSelector.selector,
    modifier: spaceSelector.modifier,
    declarations: [
      {
        property: spaceSelector.property,
        value: spaceSelector.value,
      },
    ],
  };
});

export const paddingCrossRules = Object.values(
  getSpacingSelectors('padding', ['x', 'y'], spacesWithZero, 'shortSides')
).map(spaceSelector => {
  return {
    selector: spaceSelector.selector,
    modifier: spaceSelector.modifier,
    declarations: [
      {
        property:
          spaceSelector.property === 'padding-x'
            ? 'padding-left'
            : 'padding-top',
        value: spaceSelector.value,
      },
      {
        property:
          spaceSelector.property === 'padding-x'
            ? 'padding-right'
            : 'padding-bottom',
        value: spaceSelector.value,
      },
    ],
  };
});

export const borderRules = ['', ...sides].map(side => {
  const selector =
    'border' + side.replace(side.charAt(0), side.charAt(0).toUpperCase());

  const property = `border${side && `-${side}`}`;

  return {
    selector,
    declarations: [
      {
        property,
        value: `1px solid var(--lns-color-border)`,
      },
    ],
  };
});

const displayOptions = [
  'inline',
  'block',
  'flex',
  'inlineBlock',
  'inlineFlex',
  'none',
];

export const displayRules = displayOptions.map(displayOption => {
  return {
    selector: displayOption,
    declarations: [
      {
        property: 'display',
        value: pascalCaseToKebabCase(displayOption),
      },
    ],
  };
});

export const flexWrapRules = [
  {
    selector: 'flexWrap',
    declarations: [
      {
        property: 'flex-wrap',
        value: 'wrap',
      },
    ],
  },
];

const flexDirections = ['column', 'row'];

export const flexDirectionRules = flexDirections.map(flexDirection => {
  return {
    selector: 'flexDirection',
    modifier: flexDirection,
    declarations: [
      {
        property: 'flex-direction',
        value: flexDirection,
      },
    ],
  };
});

const flexItemOptions = [
  'stretch',
  'center',
  'baseline',
  'flexStart',
  'flexEnd',
  'selfStart',
  'selfEnd',
];

export const flexItemRules = flexItemOptions.map(flexItemOption => {
  return {
    selector: 'items',
    modifier: flexItemOption,
    declarations: [
      {
        property: 'align-items',
        value: pascalCaseToKebabCase(flexItemOption),
      },
    ],
  };
});

const justifyContentOptions = [
  'flexStart',
  'flexEnd',
  'center',
  'spaceBetween',
  'spaceAround',
  'spaceEvenly',
];

export const JustifyContentRules = justifyContentOptions.map(
  justifyContentOption => {
    return {
      selector: 'justify',
      modifier: justifyContentOption,
      declarations: [
        {
          property: 'justify-content',
          value: pascalCaseToKebabCase(justifyContentOption),
        },
      ],
    };
  }
);

const growOptions = ['0', '1'];

export const growRules = growOptions.map(growOption => {
  return {
    selector: 'grow',
    modifier: growOption,
    declarations: [
      {
        property: 'flex-grow',
        value: growOption,
      },
    ],
  };
});

const shrinkOptions = ['0', '1'];

export const shrinkRules = shrinkOptions.map(shrinkOption => {
  return {
    selector: 'shrink',
    modifier: shrinkOption,
    declarations: [
      {
        property: 'flex-shrink',
        value: shrinkOption,
      },
    ],
  };
});

const alignSelfOptions = [
  'auto',
  'flexStart',
  'flexEnd',
  'center',
  'baseline',
  'stretch',
];

export const alignSelfRules = alignSelfOptions.map(alignSelfOption => {
  return {
    selector: 'self',
    modifier: alignSelfOption,
    declarations: [
      {
        property: 'align-self',
        value: pascalCaseToKebabCase(alignSelfOption),
      },
    ],
  };
});

const overflowOptions = ['hidden', 'auto'];

export const overflowRules = overflowOptions.map(overflowOption => {
  return {
    selector: 'overflow',
    modifier: overflowOption,
    declarations: [
      {
        property: 'overflow',
        value: overflowOption,
      },
    ],
  };
});

const positionOptions = ['relative', 'absolute', 'sticky', 'fixed'];

export const positionRules = positionOptions.map(positionOption => {
  return {
    selector: positionOption,
    declarations: [
      {
        property: 'position',
        value: positionOption,
      },
    ],
  };
});

export const sidePositionRules = Object.values(
  getSpacingSelectors('', sides, spacesWithAutoAndZero)
).map(spaceSelector => {
  return {
    selector: spaceSelector.selector,
    modifier: spaceSelector.modifier,
    declarations: [
      {
        property: spaceSelector.property,
        value: spaceSelector.value,
      },
    ],
  };
});

const widthOptions = ['auto', 'full', '0'];

export const widthRules = widthOptions.map(widthOption => {
  return {
    selector: 'width',
    modifier: widthOption,
    declarations: [
      {
        property: 'width',
        value: widthOption === 'full' ? '100%' : widthOption,
      },
    ],
  };
});

export const minWidthRules = [
  {
    selector: 'minWidth',
    modifier: '0',
    declarations: [
      {
        property: 'min-width',
        value: '0',
      },
    ],
  },
];

const heightOptions = ['auto', 'full', '0'];

export const heightRules = heightOptions.map(heightOption => {
  return {
    selector: 'height',
    modifier: heightOption,
    declarations: [
      {
        property: 'height',
        value: heightOption === 'full' ? '100%' : heightOption,
      },
    ],
  };
});

export const ellipsisRules = [
  {
    selector: 'ellipsis',
    declarations: [
      {
        property: 'overflow',
        value: 'hidden',
      },
      {
        property: 'text-overflow',
        value: 'ellipsis',
      },
      {
        property: 'white-space',
        value: 'nowrap',
      },
    ],
  },
];

export const accessibilityRules = [
  {
    selector: 'srOnly',
    declarations: [
      { property: 'position', value: 'absolute' },
      { property: 'width', value: '1px' },
      { property: 'height', value: '1px' },
      { property: 'padding', value: '0' },
      { property: 'margin', value: '-1px' },
      { property: 'overflow', value: 'hidden' },
      { property: 'clip', value: 'rect(0, 0, 0, 0)' },
      { property: 'white-space', value: 'nowrap' },
      { property: 'border-width', value: '0' },
    ],
  },
];
